import React, { useState, useEffect, useMemo } from 'react';
import {
  X, Edit2, Star, Phone, Mail, MapPin, Building2, Clock,
  CreditCard, FileText, CheckCircle, Tag, Wrench, Car,
  Plus, Trash2, AlertTriangle, Shield
} from 'lucide-react';
import { supplierContractAPI } from '../../api/suppliers.js';
import SupplierContractFormModal from './SupplierContractFormModal.jsx';
import { useOrgCurrency } from '../../hooks/useOrgCurrency.js';
import { useFormat } from '../../i18n/format.js';
import { useTranslation } from 'react-i18next';
import PartnerLinkDot from './PartnerLinkDot.jsx';



const STATUS_BADGE = {
  draft: 'bg-gray-100 text-gray-700',
  active: 'bg-green-100 text-green-800',
  suspended: 'bg-amber-100 text-amber-800',
  expired: 'bg-red-100 text-red-800',
  terminated: 'bg-gray-200 text-gray-500 line-through',
  renewed: 'bg-blue-100 text-blue-800',
};
// Status/type codes — labels resolved at render via t('suppliers.detail.contracts.*') below.
const STATUS_CODES = ['draft', 'active', 'suspended', 'expired', 'terminated', 'renewed'];
const TYPE_CODES = [
  'maintenance', 'leasing', 'fuel', 'tire', 'insurance_broker', 'towing',
  'glass', 'bodywork', 'parking', 'toll', 'washing', 'inspection', 'rental',
  'parts', 'other',
];

function SupplierDetailModal({
  supplier, onClose, onEdit, categoryLabels, categoryColors, categoryIcons
}) {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('fiche');
  const [contracts, setContracts] = useState([]);
  const [loadingContracts, setLoadingContracts] = useState(false);
  const [contractForm, setContractForm] = useState({ open: false, contract: null });

  if (!supplier) return null;

  const CategoryIcon = categoryIcons?.[supplier.category] || Building2;
  const categoryColor = categoryColors?.[supplier.category] || 'bg-gray-100 text-gray-800 border-gray-300';
  const categoryLabel = categoryLabels?.[supplier.category] || supplier.category;

  const loadContracts = async () => {
    setLoadingContracts(true);
    try {
      const res = await supplierContractAPI.getBySupplier(supplier.id);
      setContracts(res?.data || []);
    } catch { /* ignore */ }
    finally { setLoadingContracts(false); }
  };

  // eslint-disable-next-line react-hooks/rules-of-hooks
  useEffect(() => { if (activeTab === 'contrats') loadContracts(); }, [activeTab, supplier.id]);

  const handleDeleteContract = async (id) => {
    if (!window.confirm(t('suppliers.detail.contracts.deleteConfirm'))) return;
    await supplierContractAPI.delete(id);
    loadContracts();
  };

  // Contrats tab hidden until post-salon (code ready, not wired yet — needs doc attachments, cost engine, calendar events)
  const tabs = [
    { id: 'fiche', label: t('suppliers.detail.tabs.fiche') },
    // { id: 'contrats', label: t('suppliers.detail.contracts.title'), count: contracts.length },
  ];

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="px-6 py-4 border-b bg-gradient-to-r from-amber-50 to-orange-50 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`p-3 rounded-xl ${categoryColor.replace('text-', 'bg-').split(' ')[0]}`}>
              <CategoryIcon className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                {supplier.name}
                {supplier.is_preferred && <Star className="w-5 h-5 text-yellow-500 fill-yellow-500" />}
                <PartnerLinkDot supplier={supplier} />
              </h2>
              <p className="text-sm text-gray-500">{supplier.code}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={onEdit} className="p-2 text-amber-600 hover:bg-amber-100 rounded-lg" title={t('common.actions.edit')}><Edit2 className="w-5 h-5" /></button>
            <button onClick={onClose} className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg"><X className="w-5 h-5" /></button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200 px-6">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-amber-500 text-amber-700'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab.label}
              {tab.count > 0 && (
                <span className="ml-1.5 px-1.5 py-0.5 text-xs rounded-full bg-gray-200 text-gray-600">{tab.count}</span>
              )}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {activeTab === 'fiche' && <FicheTab supplier={supplier} categoryColor={categoryColor} categoryLabel={categoryLabel} CategoryIcon={CategoryIcon} />}
          {activeTab === 'contrats' && (
            <ContratsTab
              contracts={contracts}
              loading={loadingContracts}
              onAdd={() => setContractForm({ open: true, contract: null })}
              onEdit={(c) => setContractForm({ open: true, contract: c })}
              onDelete={handleDeleteContract}
            />
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t bg-gray-50 flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 text-gray-700 hover:bg-gray-200 rounded-lg">{t('common.actions.close')}</button>
          <button onClick={onEdit} className="px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 flex items-center gap-2">
            <Edit2 className="w-4 h-4" /> {t('common.actions.edit')}
          </button>
        </div>
      </div>

      {/* Contract form modal */}
      <SupplierContractFormModal
        isOpen={contractForm.open}
        onClose={() => setContractForm({ open: false, contract: null })}
        onSaved={loadContracts}
        supplierId={supplier.id}
        contract={contractForm.contract}
      />
    </div>
  );
}

// ─── Fiche Tab (existing content, extracted) ───────────────────────
function FicheTab({ supplier, categoryColor, categoryLabel, CategoryIcon }) {
  const { t } = useTranslation();
  const { money } = useOrgCurrency();
  const { fmt } = useFormat();
  return (
    <>
      {/* Category & Status */}
      <div className="flex items-center gap-3 flex-wrap">
        <span className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium border ${categoryColor}`}>
          <CategoryIcon className="w-4 h-4" /> {categoryLabel}
        </span>
        <span className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-sm font-medium ${
          supplier.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
        }`}>
          <CheckCircle className="w-4 h-4" />
          {supplier.status === 'active' ? t('suppliers.detail.status.active') : supplier.status}
        </span>
        {supplier.rating > 0 && (
          <span className="inline-flex items-center gap-1 px-3 py-1.5 bg-yellow-100 text-yellow-800 rounded-full text-sm font-medium">
            <Star className="w-4 h-4 fill-yellow-500" /> {Number(supplier.rating).toFixed(1)} / 5
          </span>
        )}
      </div>

      {/* Contact Info */}
      <div className="bg-gray-50 rounded-lg p-4 space-y-3">
        <h3 className="font-semibold text-gray-900 flex items-center gap-2"><Phone className="w-4 h-4" /> {t('suppliers.detail.contact.title')}</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
          {supplier.contact_name && (
            <div className="flex items-start gap-2">
              <Building2 className="w-4 h-4 text-gray-400 mt-0.5" />
              <div><p className="text-gray-500">{t('suppliers.detail.contact.name')}</p><p className="font-medium">{supplier.contact_name}</p></div>
            </div>
          )}
          {supplier.email && (
            <div className="flex items-start gap-2">
              <Mail className="w-4 h-4 text-gray-400 mt-0.5" />
              <div><p className="text-gray-500">{t('suppliers.detail.contact.email')}</p><a href={`mailto:${supplier.email}`} className="font-medium text-blue-600 hover:underline">{supplier.email}</a></div>
            </div>
          )}
          {supplier.phone && (
            <div className="flex items-start gap-2">
              <Phone className="w-4 h-4 text-gray-400 mt-0.5" />
              <div><p className="text-gray-500">{t('suppliers.detail.contact.phone')}</p><a href={`tel:${supplier.phone}`} className="font-medium text-blue-600 hover:underline">{supplier.phone}</a></div>
            </div>
          )}
          {(supplier.address || supplier.city) && (
            <div className="flex items-start gap-2 md:col-span-2">
              <MapPin className="w-4 h-4 text-gray-400 mt-0.5" />
              <div>
                <p className="text-gray-500">{t('suppliers.detail.contact.address')}</p>
                <p className="font-medium">
                  {supplier.address && <span>{supplier.address}<br /></span>}
                  {supplier.postal_code} {supplier.city}
                  {supplier.country && supplier.country !== 'FR' && ` - ${supplier.country}`}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {supplier.payment_terms > 0 && (
        <div className="bg-blue-50 rounded-lg p-4">
          <h3 className="font-semibold text-gray-900 flex items-center gap-2 mb-2"><CreditCard className="w-4 h-4" /> {t('suppliers.detail.payment.title')}</h3>
          <p className="text-sm"><span className="font-medium">{t('suppliers.detail.payment.days', { count: supplier.payment_terms })}</span> <span className="text-gray-600">{t('suppliers.detail.payment.afterInvoice')}</span></p>
        </div>
      )}

      {supplier.services?.length > 0 && (
        <div className="bg-orange-50 rounded-lg p-4">
          <h3 className="font-semibold text-gray-900 flex items-center gap-2 mb-3"><Wrench className="w-4 h-4" /> {t('suppliers.detail.services.title', { count: supplier.services.length })}</h3>
          <div className="flex flex-wrap gap-2">
            {supplier.services.map(s => (
              <span key={s.id} className="inline-flex items-center gap-1 px-2 py-1 bg-white border border-orange-200 rounded text-sm">
                <Tag className="w-3 h-3 text-orange-500" /> {s.service_name}
                {s.price > 0 && <span className="text-gray-500 ml-1">({money(s.price, supplier)})</span>}
              </span>
            ))}
          </div>
        </div>
      )}

      {supplier.makes?.length > 0 && (
        <div className="bg-purple-50 rounded-lg p-4">
          <h3 className="font-semibold text-gray-900 flex items-center gap-2 mb-3"><Car className="w-4 h-4" /> {t('suppliers.detail.makes.title', { count: supplier.makes.length })}</h3>
          <div className="flex flex-wrap gap-2">
            {supplier.makes.map(m => (
              <span key={m.id} className="inline-flex items-center gap-1 px-2 py-1 bg-white border border-purple-200 rounded text-sm">
                <CheckCircle className="w-3 h-3 text-purple-500" /> {m.brand_name}
                {m.certification_level && <span className="text-xs text-purple-600 ml-1">({m.certification_level})</span>}
              </span>
            ))}
          </div>
        </div>
      )}

      {supplier.notes && (
        <div className="bg-gray-50 rounded-lg p-4">
          <h3 className="font-semibold text-gray-900 flex items-center gap-2 mb-2"><FileText className="w-4 h-4" /> {t('suppliers.detail.notes.title')}</h3>
          <p className="text-sm text-gray-700 whitespace-pre-wrap">{supplier.notes}</p>
        </div>
      )}

      <div className="text-xs text-gray-400 flex items-center gap-4 pt-4 border-t">
        <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {t('suppliers.detail.createdOn', { date: fmt.date(supplier.created_at) })}</span>
        {supplier.updated_at && <span>{t('suppliers.detail.updatedOn', { date: fmt.date(supplier.updated_at) })}</span>}
      </div>
    </>
  );
}

// ─── Contrats Tab ──────────────────────────────────────────────────
function ContratsTab({ contracts, loading, onAdd, onEdit, onDelete }) {
  const { t } = useTranslation();
  // Devise de l'organisation du contrat fournisseur — un seul outil monétaire.
  const { money } = useOrgCurrency();
  const statusLabels = useMemo(
    () => Object.fromEntries(STATUS_CODES.map((c) => [c, t(`suppliers.detail.contracts.statuses.${c}`)])),
    [t]
  );
  const typeLabels = useMemo(
    () => Object.fromEntries(TYPE_CODES.map((c) => [c, t(`suppliers.detail.contracts.types.${c}`)])),
    [t]
  );
  if (loading) return <p className="text-sm text-gray-500 py-4">{t('suppliers.detail.contracts.loading')}</p>;

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="font-semibold text-gray-900 flex items-center gap-2">
          <Shield className="w-4 h-4 text-blue-600" /> {t('suppliers.detail.contracts.title', { count: contracts.length })}
        </h3>
        <button onClick={onAdd} className="px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 flex items-center gap-1">
          <Plus className="w-4 h-4" /> {t('suppliers.detail.contracts.add')}
        </button>
      </div>

      {contracts.length === 0 ? (
        <div className="text-center text-gray-400 py-8">
          <FileText className="w-10 h-10 mx-auto mb-2 text-gray-300" />
          <p>{t('suppliers.detail.contracts.empty')}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {contracts.map(c => {
            const isExpiringSoon = c.end_date && new Date(c.end_date) < new Date(Date.now() + 30 * 86400000) && c.status === 'active';
            return (
              <div key={c.id} className={`bg-white border rounded-lg p-4 ${isExpiringSoon ? 'border-amber-300 bg-amber-50' : 'border-gray-200'}`}>
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-mono text-sm font-semibold text-gray-900">{c.contract_reference}</span>
                      <span className={`px-2 py-0.5 text-xs rounded-full font-medium ${STATUS_BADGE[c.status] || 'bg-gray-100 text-gray-700'}`}>
                        {statusLabels[c.status] || c.status}
                      </span>
                      {isExpiringSoon && (
                        <span className="text-xs text-amber-700 flex items-center gap-1">
                          <AlertTriangle className="w-3 h-3" /> {t('suppliers.detail.contracts.expiringSoon')}
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-600">
                      {typeLabels[c.contract_type] || c.contract_type}
                      {c.amount_annual_ht > 0 && <> — <span className="font-medium">{t('suppliers.detail.contracts.perYearExclTax', { amount: money(c.amount_annual_ht, c) })}</span></>}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      {c.start_date} → {c.end_date || t('suppliers.detail.contracts.indefinite')}
                      {c.auto_renew ? ` (${t('suppliers.detail.contracts.autoRenew')})` : ''}
                    </p>
                  </div>
                  <div className="flex gap-1">
                    <button onClick={() => onEdit(c)} className="p-1 text-blue-500 hover:text-blue-700" title={t('common.actions.edit')}><Edit2 className="w-4 h-4" /></button>
                    <button onClick={() => onDelete(c.id)} className="p-1 text-red-400 hover:text-red-600" title={t('common.actions.delete')}><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>

                {/* SLA indicators */}
                {(c.sla_response_hours || c.sla_resolution_hours) && (
                  <div className="flex gap-4 mt-3 pt-3 border-t border-gray-100 text-xs">
                    {c.sla_response_hours && (
                      <div>
                        <span className="text-gray-500">{t('suppliers.detail.contracts.slaResponse')}</span>{' '}
                        <span className="font-medium">{c.sla_response_hours}h</span>
                        {c.sla_compliance_pct != null && (
                          <span className={`ml-1 font-bold ${c.sla_compliance_pct >= 90 ? 'text-green-600' : c.sla_compliance_pct >= 70 ? 'text-amber-600' : 'text-red-600'}`}>
                            ({c.sla_compliance_pct}%)
                          </span>
                        )}
                      </div>
                    )}
                    {c.sla_resolution_hours && (
                      <div>
                        <span className="text-gray-500">{t('suppliers.detail.contracts.slaResolution')}</span>{' '}
                        <span className="font-medium">{c.sla_resolution_hours}h</span>
                      </div>
                    )}
                  </div>
                )}

                {c.terms_summary && (
                  <p className="text-xs text-gray-500 mt-2 italic">{c.terms_summary}</p>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default SupplierDetailModal;
